import { Space } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Alert, Button, Modal, Popconfirm } from 'ui/base';

import List from './List';

const DEFAULT_CONFIRM_MESSAGE = {
  id: 'page.fiche.link_with_audits_modal.warning',
  defaultMessage: 'Warning, this action will update your works.',
};

/**
 * @typedef {{
 *  description?: React.ReactNode,
 *  alertMessage?: React.ReactNode,
 *  onClose: () => void,
 *  onConfirm: (event: React.MouseEvent) => void,
 *  search?: React.ReactNode,
 *  title?: React.ReactNode,
 *  okButtonProps?: import('antd').ButtonProps,
 * } & import('./List').ListProps} ImportModalProps
 */

/** @type {React.FC<ImportModalProps>} */
const ImportModal = ({
  title,
  search,
  onClose,
  onConfirm,
  description,
  alertMessage,
  okButtonProps,
  confirmMessage = <FormattedMessage {...DEFAULT_CONFIRM_MESSAGE} />,
  ...listProps
}) => {
  const renderFooter = React.useCallback(
    (_, { CancelBtn }) => (
      <Space>
        <CancelBtn />
        <Popconfirm placement="left" onConfirm={onConfirm} title={confirmMessage}>
          <Button type="primary" {...okButtonProps}>
            <FormattedMessage id="import" defaultMessage="Import" />
          </Button>
        </Popconfirm>
      </Space>
    ),
    [onConfirm, confirmMessage, okButtonProps]
  );

  return (
    <Modal
      open
      onOk={() => {}}
      title={title}
      okText={<FormattedMessage id="import" defaultMessage="Import" />}
      onCancel={onClose}
      data-cy="import-modal"
      footer={renderFooter}
      okButtonProps={okButtonProps}
      classNames={{ body: 'd-flex flex-column gap-3' }}
    >
      {alertMessage && (
        <Alert type="warning" className="mb-3" title={alertMessage} />
      )}

      <div className="d-flex flex-column gap-1">
        {description && (
          <label htmlFor="import-form-search">
            {description}
          </label>
        )}
        {search}
      </div>
      <List {...listProps} />
    </Modal>
  );
};

export default ImportModal;
